import { observer } from "mobx-react";
import { FC } from "react";
import {
  CollapseList,
  NumberField,
  NumberQuestion,
  TextAreaQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { ConvertibleLoanPowerOfAttorneyForm } from "./convertibleLoanPoADomain";
import { VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { CURRENCY_NOK } from "@/utils/currency";

import { useTranslation } from "@/stores/RootStoreContext";
import { RadioFieldQuestion } from "@/common/commonComponents";
import { findShareTypeName } from "@/common/share";

export const ConvertibleLoanPowerOfAttorneyView: FC<{
  form: ConvertibleLoanPowerOfAttorneyForm;
}> = observer(({ form }) => {
  const { t, lang } = useTranslation();

  return (
    <VBox gap={"md"}>
      <NumberQuestion
        question={t("decisions.convertibleLoanPoA.convertibleLoanValue")}
        prefix={CURRENCY_NOK}
        field={form.convertibleLoanValue}
        isLocked={form.isProjectLocked}
        inline
      />
      <NumberQuestion
        question={t("decisions.convertibleLoanPoA.convertibleLoanCapitalShareValue.title")}
        prefix={CURRENCY_NOK}
        field={form.convertibleLoanCapitalShareValue}
        isLocked={form.isProjectLocked}
        inline
      />
      <Alert
        type="info"
        message={t("decisions.convertibleLoanPoA.convertibleLoanCapitalShareValue.helperText")}
      />
      <VBox>
        <div>{t("decisions.convertibleLoanPoA.shareClass.title")}</div>
        <Checkbox
          label={t("decisions.convertibleLoanPoA.shareClass.all")}
          onChange={() => form.toggleAllShareTypes()}
          checked={form.isAllShareTypesSelected}
          disabled={form.isProjectLocked}
        />
        <CollapseList
          closeLabel={t("collapse")}
          openLabel={t("dbot.showAll")}
          initiallyExpanded={form.isShareTypesExpanded}
        >
          <MCheckboxGroup
            model={form.convertibleLoanShareTypes}
            disabled={form.isProjectLocked}
            labelFn={(shareType) => findShareTypeName(shareType, lang)}
          />
        </CollapseList>
        <Checkbox
          label={t("decisions.convertibleLoanPoA.shareClass.newClass")}
          onChange={(event) => {
            form.convertibleLoanNewShareType.setValue(event.target.checked);
          }}
          checked={form.convertibleLoanNewShareType.value}
          disabled={form.isProjectLocked}
        />
      </VBox>
      {form.showNewShareTypeDescriptionInput && (
        <TextAreaQuestion
          question={t("decisions.convertibleLoanPoA.convertibleLoanNewShareTypeDescription.title")}
          field={form.convertibleLoanNewShareTypeDescription}
          isLocked={form.isProjectLocked}
        />
      )}
      {form.showShareholderRightsQuestion && (
        <YesNoQuestion
          question={t("decisions.convertibleLoanPoA.deviateFromShareholderRights")}
          field={form.convertibleLoanDeviateFromShareholderRights}
          isLocked={form.isProjectLocked}
        />
      )}
      {form.showDurationQuestion && (
        <VBox>
          <RadioFieldQuestion
            question={{
              key: "decisions.convertibleLoanPoA.convertibleLoanDuration.questionText",
            }}
            field={form.convertibleLoanDuration}
            fieldName="convertibleLoanDuration"
            isLocked={form.isProjectLocked}
          />
        </VBox>
      )}
      {form.showDurationMonthsInput && (
        <>
          <NumberField
            label={t("decisions.convertibleLoanPoA.convertibleLoanDurationMonths.label")}
            field={form.convertibleLoanDurationMonths}
            isLocked={form.isProjectLocked}
            inline
          />
          {form.invalidDurationMonths && (
            <Alert
              type={"error"}
              message={t("decisions.convertibleLoanPoA.convertibleLoanDurationMonths.error")}
            />
          )}
          <Alert
            type="info"
            message={t("decisions.convertibleLoanPoA.convertibleLoanDurationMonths.helperText")}
          />
        </>
      )}
      {form.showReasonInput && (
        <TextAreaQuestion
          question={t("decisions.convertibleLoanPoA.convertibleLoanReason")}
          field={form.convertibleLoanReason}
          isLocked={form.isProjectLocked}
        />
      )}
    </VBox>
  );
});
